import React, { useEffect, useMemo } from "react";
import { Link, useParams } from "react-router-dom";
import BlogArticleBody from "../components/BlogArticleBody";
import BlogCTA from "../components/blog/BlogCTA";
import { blogPosts, formatBlogDate, getBlogPostBySlug } from "../data/blogPosts";

const MID_BLOG_CTA_PROPS = {
  title: "Thinking about buying a renovation property?",
  description:
    "Send me the listing and a quick note on your goals — I’ll give you a straight read on fit, risk, and potential before you commit.",
  primaryLabel: "Start a conversation",
  primaryHref: "/contact",
  secondaryLabel: "View portfolio",
  secondaryHref: "/portfolio",
};

const END_BLOG_CTA_PROPS = {
  title: "Want a second opinion before you commit?",
  description:
    "A 10-minute conversation can often uncover issues or opportunities that aren’t obvious at first — especially with renovation properties.",
  primaryLabel: "Contact",
  primaryHref: "/contact",
  secondaryLabel: "View portfolio",
  secondaryHref: "/portfolio",
};

const articleLinkClass =
  "font-medium text-slate-800 underline decoration-slate-300 underline-offset-2 transition hover:text-slate-600";

const MICRO_BEFORE_MID_CTA = "This is where better early decisions matter.";

/**
 * Split article text after the second ## section when possible; otherwise ~35% at a paragraph boundary.
 * Keeps split logic aligned with BlogArticleBody block boundaries (\\n\\n).
 */
function splitBlogContentForMidCta(content) {
  const trimmed = (content || "").trim();
  if (!trimmed) return { before: "", after: "" };

  const blocks = trimmed.split(/\n\n+/);
  let secondHeadingIdx = -1;
  let headingCount = 0;
  for (let i = 0; i < blocks.length; i++) {
    if (blocks[i].trim().startsWith("## ")) {
      headingCount += 1;
      if (headingCount === 2) {
        secondHeadingIdx = i;
        break;
      }
    }
  }

  if (secondHeadingIdx >= 0) {
    let j = secondHeadingIdx + 1;
    while (j < blocks.length && !blocks[j].trim().startsWith("## ")) {
      j += 1;
    }
    const endSecond = j - 1;
    if (endSecond < blocks.length - 1) {
      return {
        before: blocks.slice(0, endSecond + 1).join("\n\n"),
        after: blocks.slice(endSecond + 1).join("\n\n"),
      };
    }
  }

  const target = Math.floor(trimmed.length * 0.35);
  let splitAt = -1;
  for (let pos = 0; pos < trimmed.length - 1; pos += 1) {
    if (trimmed[pos] === "\n" && trimmed[pos + 1] === "\n") {
      if (pos + 2 <= target) splitAt = pos + 2;
      else break;
    }
  }
  if (splitAt <= 0) {
    let idx = trimmed.indexOf("\n\n", target);
    if (idx === -1) idx = trimmed.indexOf("\n\n", 0);
    if (idx !== -1) splitAt = idx + 2;
  }
  if (splitAt <= 0 || splitAt >= trimmed.length) {
    return { before: trimmed, after: "" };
  }
  return {
    before: trimmed.slice(0, splitAt).trimEnd(),
    after: trimmed.slice(splitAt).trimStart(),
  };
}

function BlogDetail() {
  const { slug } = useParams();
  const pub = process.env.PUBLIC_URL || "";

  const post = useMemo(() => getBlogPostBySlug(slug || ""), [slug]);

  useEffect(() => {
    if (post?.seoTitle) {
      document.title = `${post.seoTitle} | Mark Beattie`;
    }
    return () => {
      document.title = "Mark Beattie";
    };
  }, [post]);

  const related = useMemo(() => {
    if (!post) return [];
    return blogPosts
      .filter((p) => p.slug !== post.slug && p.category === post.category)
      .slice(0, 2);
  }, [post]);

  const { before: contentBeforeMidCta, after: contentAfterMidCta } = useMemo(
    () => splitBlogContentForMidCta(post?.content || ""),
    [post]
  );

  const showMicroBeforeMidCta = useMemo(() => {
    const c = post?.content || "";
    return c.length > 0 && !/better early decisions matter/i.test(c);
  }, [post]);

  if (!post) {
    return (
      <div className="bg-secondary min-h-[50vh]">
        <div className="mx-auto max-w-3xl px-4 py-10">
          <p className="text-slate-600">Article not found.</p>
          <Link
            to="/blog"
            className="mt-4 inline-block font-medium text-slate-700 underline decoration-slate-300 underline-offset-2"
          >
            ← Back to Blog
          </Link>
        </div>
      </div>
    );
  }

  const heroImg = `${pub}/${post.featuredImage}`;

  return (
    <div className="bg-secondary">
      <article>
        <div className="border-b border-slate-200 bg-white">
          <div className="mx-auto max-w-4xl px-4 py-7 md:py-9">
            <Link
              to="/blog"
              className="inline-block text-sm font-medium text-slate-600 underline decoration-slate-300 underline-offset-2 transition hover:text-slate-900"
            >
              ← Back to Blog
            </Link>
            <p className="mt-6 text-xs font-semibold uppercase tracking-wide text-slate-500">{post.category}</p>
            <h1 className="mt-2 text-2xl font-semibold leading-tight tracking-tight text-slate-900 md:text-3xl md:leading-snug">
              {post.title}
            </h1>
            <div className="mt-4 flex flex-wrap items-center gap-3 text-sm text-slate-500">
              <time dateTime={post.date}>{formatBlogDate(post.date)}</time>
              <span aria-hidden="true">·</span>
              <span>{post.readTime}</span>
            </div>
            <p className="mt-5 max-w-2xl text-base leading-relaxed text-slate-600 md:text-lg">{post.excerpt}</p>
          </div>
        </div>

        <div className="relative mx-auto max-w-5xl border-b border-slate-200/80 bg-slate-100">
          <div className="aspect-[21/9] w-full max-h-[380px] md:aspect-[2.4/1]">
            <img
              src={heroImg}
              alt=""
              className="h-full w-full object-cover object-center"
              loading="eager"
              decoding="async"
            />
          </div>
        </div>

        <div className="mx-auto max-w-3xl px-4 py-8 md:py-10">
          <BlogArticleBody content={contentBeforeMidCta} />
          {showMicroBeforeMidCta ? (
            <p className="mt-4 max-w-2xl text-[15px] leading-relaxed text-slate-700 md:text-base">
              {MICRO_BEFORE_MID_CTA}
            </p>
          ) : null}
          <BlogCTA {...MID_BLOG_CTA_PROPS} />
          {contentAfterMidCta.trim() ? (
            <BlogArticleBody content={contentAfterMidCta} />
          ) : null}
          <p className="mt-4 max-w-2xl text-[15px] leading-relaxed text-slate-700 md:text-base">
            You can also see how this thinking shows up in real projects in my{" "}
            <Link to="/portfolio" className={articleLinkClass}>
              portfolio
            </Link>
            .
          </p>
          <BlogCTA {...END_BLOG_CTA_PROPS} />
        </div>

        {related.length > 0 ? (
          <div className="border-t border-slate-200/70 bg-secondary">
            <div className="mx-auto max-w-3xl px-4 py-8 md:py-10">
              <h2 className="text-lg font-semibold text-slate-900">Related on this topic</h2>
              <ul className="mt-4 space-y-3">
                {related.map((p) => (
                  <li key={p.id}>
                    <Link
                      to={`/blog/${encodeURIComponent(p.slug)}`}
                      className="font-medium text-slate-800 underline decoration-slate-300 underline-offset-2 transition hover:text-slate-600"
                    >
                      {p.title}
                    </Link>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        ) : null}
      </article>
    </div>
  );
}

export default BlogDetail;
